// RBAC track: the manager-approval UI flow's PURE decision logic, split
// out from ui/src/main.ts (which owns the actual screen wiring -- showing
// and hiding #approval-picker-screen/#pin-entry-screen, calling the
// server, and retrying the guarded request) so the decisions below can be
// unit tested with no DOM at all, mirroring test/
// ui-pay-idempotency-key.test.ts's own "why a plain node:test file can
// import a ui/src module directly" reasoning: neither function here
// touches `window` or `document`.
//
// The server side of this flow is src/domain/permissions.ts's
// requirePermission/ApprovalRequiredError/recordPermissionApproval: a
// first call to a gated route with no approverUserId/approverPin gets
// HTTP 403 {error: "approval_required", permissionId: "<id>"}; a retry
// carrying those two fields in the SAME request body (same idempotencyKey
// too, so recordPermissionApproval's own audit row is not duplicated by a
// retry) is verified server-side and, if it checks out, the guarded
// action itself runs. See ui/src/main.ts's own "Manager approval" section
// for the three live call sites this powers (Reopen tab, Remove saved
// card, Close count session) and index.html's own
// #approval-picker-screen for the markup this drives.

import type { ApiErrorBody, ApprovalCandidateDto } from "./api.ts";
import { sortByName } from "./nameSort.ts";

export interface ApprovalCandidateSource {
  id: string;
  displayName: string;
  /** The user's PRIMARY role as stored (users.role). Display text only,
   * and only on the degraded path -- see approvalCandidateBadgeRole and
   * selectApprovalCandidates below. Never used to decide who may
   * approve.
   *
   * OPTIONAL only because this type is shared with `UserDto`
   * (ui/src/api.ts), whose `role` is absent solely for the fully
   * anonymous, no-session-at-all caller. The degraded path's roster
   * fallback always runs under an authenticated session (requireSession
   * in main.ts's openApprovalPicker), so it can never receive that
   * shape; `role` is always present here at runtime. */
  role?: string;
  /** The user's full stored role set (user_roles), when the roster the
   * fallback read returned carried one. Display text only, same as
   * `role`. Optional because GET .../users answers with two different
   * shapes: a caller holding admin.user.view gets the full UserDto (this
   * field populated), and any other caller gets src/api/server.ts's own
   * toPreLoginUserResponse minimal shape (id/displayName/role only, this
   * field absent). */
  roles?: readonly string[];
}

/** What the picker should show, and how much it is entitled to claim.
 *
 *   - "filtered": the server answered GET .../approval-candidates, so
 *     this list is exactly the set requirePermission will accept as an
 *     approver for this permission, and the picker can say so by saying
 *     nothing.
 *   - "unverified": that read did not succeed, so this device cannot
 *     tell who can approve. Everyone else on shift is offered and the
 *     picker says plainly that it could not check. See
 *     selectApprovalCandidates below for why this is not a silent
 *     fallback to role names.
 */
export type ApprovalCandidateSelection = {
  kind: "filtered" | "unverified";
  candidates: ApprovalCandidateSource[];
};

/** The text on a candidate tile's badge, on the DEGRADED path only.
 *
 * Kept, and kept role-shaped, deliberately. A badge is a display label,
 * not a gate: nothing branches on its value, and the same tile is
 * tappable whatever it says. It is reached only when
 * selectApprovalCandidates has already answered "unverified", where the
 * list is every colleague on shift and the picker has already said in
 * plain language that it could not check who can approve. In that one
 * situation a role word is the only distinguishing thing this device
 * knows about a person, and it is genuinely useful for guessing who to
 * ask first. The filtered path has no badge at all, because there the
 * list is already exactly the people who can approve and a role word
 * would add nothing but a hint about the tenant's authority structure.
 *
 * No role NAME is compared against anything here: it reports the first
 * role the roster gave, falling back to the legacy primary column. That
 * is what keeps it data rather than a gate under CLAUDE.md's
 * permission-driven rule and scripts/check-no-role-driven-gating.ts.
 *
 * SEC3 (DEC-100): genuinely returns `undefined` now that both `roles`
 * and `role` are optional on `ApprovalCandidateSource` (the roster
 * fallback's degraded read can land on the pre-login minimal shape for
 * a non-privileged actor's own session). The caller decides what an
 * absent role means for display; this function only reports it. */
export function approvalCandidateBadgeRole(candidate: ApprovalCandidateSource): string | undefined {
  return candidate.roles?.[0] ?? candidate.role;
}

/**
 * Who the approver picker offers as a tile.
 *
 * Product-owner ruling 2026-08-28 ("Nothing EVER should be role driven
 * from that I can see. All actions should be permission driven"),
 * CLAUDE.md's permission-driven rule. This function used to filter a
 * staff roster down to users holding a hardcoded manager-tier role NAME.
 * That was a live correctness bug, not a style problem, because the
 * server's requirePermission accepts an approver on a completely
 * different test: the approver must hold THE SPECIFIC PERMISSION being
 * approved as "allowed". The two sets differ in both directions.
 *
 *   - Someone who genuinely CAN approve (an unusual role, or an
 *     individual override) was not offered. If they were the only such
 *     person on shift, the floor was hard-blocked with "No manager is
 *     available to approve this right now" while a person who could
 *     approve it stood next to the tablet.
 *   - A manager-tier user who CANNOT approve that particular permission
 *     WAS offered, and picking them failed only after they had walked
 *     over and typed their PIN.
 *
 * Once role templates are configurable (backlog R3 to R7, DEC-024) those
 * two sets stop resembling each other at all, so no amount of care in
 * the browser fixes it. The server now answers the question directly
 * (GET .../approval-candidates, which resolves the same permission
 * requirePermission does), and this function's job shrinks to turning
 * that answer, or its absence, into what the picker shows.
 *
 * `candidates` is the server's answer, or undefined when that read did
 * not succeed (any non-200, including a network failure, and including a
 * 403 approval_required body that carried no permissionId at all, which
 * is what a server older than this ruling returns). `roster` is the
 * ordinary staff list, used ONLY as the degraded fallback.
 *
 * The degraded path offers everyone but the acting user and reports
 * "unverified", which the picker surfaces as plain language. It does not
 * fall back to role names, because that reproduces exactly the wrong
 * answer this change removes; and it does not hide everyone, because
 * that hard-blocks the action. It costs at most an extra tap and never a
 * false grant: the server's own requirePermission is what actually
 * decides whether a picked approver's PIN and grant check out.
 *
 * The acting user is filtered out on both paths. The server already
 * excludes them (and requirePermission refuses self-approval outright),
 * so this is belt and braces on the authoritative path and the real
 * exclusion on the degraded one: offering that tile could only ever
 * produce a guaranteed-failing tap.
 */
export function selectApprovalCandidates(
  candidates: readonly ApprovalCandidateDto[] | undefined,
  roster: readonly ApprovalCandidateSource[],
  actingUserId: string,
): ApprovalCandidateSelection {
  if (candidates === undefined) {
    return {
      kind: "unverified",
      // UX88 (DEC-339): approvers read A to Z by name.
      candidates: sortByName(
        roster.filter((user) => user.id !== actingUserId),
        (user) => user.displayName,
      ),
    };
  }
  return {
    kind: "filtered",
    // Re-shaped into the tile's own source type so the picker renders one
    // shape either way. `role` is empty because the authoritative answer
    // deliberately carries none (see the route's own header for why), and
    // the filtered path renders no badge, so nothing reads it.
    candidates: sortByName(
      candidates
        .filter((candidate) => candidate.id !== actingUserId)
        .map((candidate) => ({ id: candidate.id, displayName: candidate.displayName, role: "" })),
      (candidate) => candidate.displayName,
    ),
  };
}

export type ApprovalRetryClassification =
  | { kind: "success" }
  | { kind: "retry_on_pad"; message: string }
  | { kind: "network_error"; message: string }
  | { kind: "return_to_picker"; message: string }
  | { kind: "other_failure"; message: string };

const NETWORK_ERROR_FALLBACK = "Could not reach the server. Check your connection and try again.";
const GENERIC_FAILURE_FALLBACK = "Something went wrong. Try again.";

/**
 * What the PIN pad's submit handler should do next, given the result of
 * retrying the original guarded request with {approverUserId,
 * approverPin} added to its body. Every branch corresponds to one of
 * src/domain/permissions.ts's requirePermission's own documented
 * approval_required outcomes, plus the two outcomes that never come from
 * that function at all (a genuine 2xx, and a request that never reached
 * the server):
 *
 *   - 2xx ("success"): the guarded action itself ran (and, per
 *     requirePermission's own contract, recordPermissionApproval already
 *     wrote the audit row naming both the acting and approving user).
 *     Nothing left for this flow to do beyond closing its own screens.
 *   - 401 invalid_pin_credentials ("retry_on_pad"): the picked approver's
 *     PIN itself did not verify. Stays on the pin pad, exactly like an
 *     ordinary login PIN mistake (main.ts's handlePinEntryFailure) --
 *     the same approver may simply have mistyped it.
 *   - network error ("network_error"): the request never reached the
 *     server, so whether the action already happened is genuinely
 *     unknown. This is NEVER auto-retried by this module or its caller;
 *     the caller stays on the pin pad so a human can choose to try
 *     again, and that manual retry is safe specifically because it
 *     resends the SAME idempotency key the first attempt used (see this
 *     file's own header) -- a genuine prior success replays instead of
 *     double-applying, per this codebase's idempotent-command design
 *     throughout (mirrors ui/src/api.ts's shouldClearPayIdempotencyKeyOnFailure
 *     reasoning for the identical "when in doubt, keep the key" property).
 *   - 423 (AccountLockedError), 403 insufficient_rights, or 403
 *     approver_pin_change_required ("return_to_picker"): four distinct
 *     server-side failures
 *     collapse to this one UI branch, because all three mean "THIS
 *     specific approver cannot approve THIS specific permission right
 *     now," never "no one can": the account is locked out (423); the
 *     picked approver was the acting user themselves (should be
 *     unreachable once selectApprovalCandidates has excluded them, but
 *     the server's own check is authoritative, so this still handles
 *     it); or the picked approver does not hold "allowed" (only
 *     "approval_required" or "denied") on this exact permission id. The
 *     server's own message text already distinguishes all three in plain
 *     language (requirePermission's own distinct error messages); this
 *     function passes it through rather than replacing it with a fourth,
 *     less specific one. The caller sends the flow back to the picker so
 *     a DIFFERENT manager can be chosen, rather than dead-ending on a pad
 *     that can never succeed for this approver right now.
 *   - Anything else ("other_failure"): also terminal, and most commonly
 *     the GUARDED ACTION's own domain error (e.g. the tab was already
 *     reopened by someone else in the meantime) rather than anything
 *     about the approval itself, which may well have verified fine. Not
 *     this module's concern to retry or reinterpret; the caller ends the
 *     flow and lets its own ordinary error handling show this message.
 */
export function classifyApprovalRetryResult(result: {
  status: number;
  networkError: boolean;
  body?: Partial<ApiErrorBody>;
}): ApprovalRetryClassification {
  if (result.networkError) {
    return { kind: "network_error", message: result.body?.message ?? NETWORK_ERROR_FALLBACK };
  }
  if (result.status === 200 || result.status === 201) {
    return { kind: "success" };
  }
  const code = result.body?.error;
  const message = result.body?.message ?? GENERIC_FAILURE_FALLBACK;
  if (result.status === 401 && code === "invalid_pin_credentials") {
    return { kind: "retry_on_pad", message };
  }
  if (result.status === 423) {
    return { kind: "return_to_picker", message };
  }
  if (result.status === 403 && code === "insufficient_rights") {
    return { kind: "return_to_picker", message };
  }
  // Gate finding 8, 2026-08-27: the picked approver has never signed in
  // and still carries the one-time temporary PIN an admin handed them
  // (src/domain/users.ts's verifyApproverPin). Same UI shape as the three
  // above for the same reason -- THIS approver cannot approve right now,
  // but a different one can -- with its own distinct server message
  // telling the actor what to do about it.
  if (result.status === 403 && code === "approver_pin_change_required") {
    return { kind: "return_to_picker", message };
  }
  return { kind: "other_failure", message };
}
